import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { LifeBuoy, Send, Loader2, Plus, UserRound, CheckCircle2, Clock } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const STARTERS = ["How do I get more matches?", "How do I buy Empire Coins?", "How do I schedule a post?", "How do tip goals work?"];
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Bubble = ({ m, i }) => (
  <div data-testid={`help-msg-${i}`} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
    <div className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-sm whitespace-pre-line ${m.role === "user" ? "gold-gradient text-black rounded-br-sm" : "bg-[#1A1A1A] text-white border border-[#2A2A2A] rounded-bl-sm"}`}>
      {m.text || <Loader2 size={14} className="animate-spin text-[#D4AF37]" />}
    </div>
  </div>
);

const Tickets = ({ tickets }) => tickets.length === 0 ? null : (
  <div className="mt-8" data-testid="help-tickets">
    <div className="overline mb-3">Your requests to our team</div>
    <div className="space-y-3">
      {tickets.map((t) => (
        <div key={t.id} data-testid={`help-ticket-${t.id}`} className="pe-card rounded-2xl p-4">
          <div className="flex items-center justify-between text-xs mb-1">
            <span className="text-[#A0A0A0]">{new Date(t.created_at).toLocaleDateString()}</span>
            {t.status === "answered" ? <span className="text-[#4ADE80] flex items-center gap-1"><CheckCircle2 size={12} /> Answered</span>
              : <span className="text-[#D4AF37] flex items-center gap-1"><Clock size={12} /> Waiting for reply</span>}
          </div>
          <div className="text-sm text-white">{t.note || t.messages?.find((m) => m.role === "user")?.text}</div>
          {t.reply && <div className="mt-2 text-sm text-[#E8E8E8] border-l-2 border-[#D4AF37] pl-3" data-testid={`help-ticket-reply-${t.id}`}>{t.reply}</div>}
        </div>
      ))}
    </div>
  </div>
);

export default function Help() {
  const [session, setSession] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [escalating, setEscalating] = useState(false);
  const [note, setNote] = useState("");
  const bottomRef = useRef(null);
  const messages = session?.messages || [];

  // Never overwrite a conversation that started before this load finished.
  const load = () => api.get("/help/session").then((r) => { setSession((s) => s || r.data.session); setTickets(r.data.tickets); }).catch(() => {});
  useEffect(() => { load(); }, []);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages.length, messages[messages.length - 1]?.text]);

  const ask = async (q) => {
    const question = (q ?? text).trim();
    if (!question || busy) return;
    setText(""); setBusy(true);
    setSession((s) => ({ id: s?.id, escalated: s?.escalated, messages: [...(s?.messages || []), { role: "user", text: question }, { role: "assistant", text: "" }] }));
    try {
      const res = await fetch(`${API}/help/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("pe_token")}` },
        body: JSON.stringify({ session_id: session?.id || null, message: question }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || "Help is busy, try again");
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n"); buf = parts.pop();
        parts.forEach((line) => {
          if (!line.startsWith("data: ")) return;
          const d = JSON.parse(line.slice(6));
          setSession((s) => {
            if (!s) return s;
            const msgs = [...s.messages];
            if (d.delta) msgs[msgs.length - 1] = { role: "assistant", text: msgs[msgs.length - 1].text + d.delta };
            return { ...s, id: d.session_id || s.id, messages: msgs };
          });
        });
      }
    } catch (e) {
      toast.error(e.message);
      setSession((s) => (s ? { ...s, messages: s.messages.slice(0, -2) } : s));
    } finally { setBusy(false); }
  };

  const newChat = async () => { const { data } = await api.post("/help/new"); setSession(data.session); setEscalating(false); };
  const escalate = async () => {
    try {
      await api.post("/help/escalate", { session_id: session.id, note });
      toast.success("Sent! Our team will reply here soon");
      setEscalating(false); setNote(""); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="p-6 md:p-12 max-w-3xl mx-auto" data-testid="help-page">
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <div className="overline mb-2 flex items-center gap-2"><LifeBuoy size={12} className="text-[#D4AF37]" /> Help centre</div>
          <h1 className="font-heading text-4xl sm:text-5xl">Ask Velvet</h1>
          <p className="text-sm text-[#A0A0A0] mt-2">Your 24/7 guide to the Empire, for members and creators alike.</p>
        </div>
        {messages.length > 0 && (
          <button data-testid="help-new-chat" onClick={newChat} className="btn-outline-gold px-4 py-2 rounded-full text-sm flex items-center gap-1.5 flex-shrink-0"><Plus size={14} /> New chat</button>
        )}
      </div>

      <div className="pe-card rounded-3xl overflow-hidden">
        <div className="h-[50vh] min-h-[320px] overflow-y-auto p-5 space-y-3" data-testid="help-messages">
          {messages.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center">
              <div className="w-14 h-14 rounded-2xl gold-gradient flex items-center justify-center mb-4"><LifeBuoy className="text-black" /></div>
              <div className="text-white mb-4">Hi, I'm Velvet. What can I help you with?</div>
              <div className="flex flex-wrap justify-center gap-2">
                {STARTERS.map((q, i) => (
                  <button key={q} data-testid={`help-starter-${i}`} onClick={() => ask(q)}
                    className="text-xs px-3 py-1.5 rounded-full border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10 transition-colors">{q}</button>
                ))}
              </div>
            </div>
          ) : messages.map((m, i) => <Bubble key={i} m={m} i={i} />)}
          <div ref={bottomRef} />
        </div>

        {messages.length > 1 && !busy && (
          <div className="px-5 py-3 border-t border-[#2A2A2A] text-xs" data-testid="help-escalate-area">
            {session?.escalated ? (
              <span className="text-[#A0A0A0] flex items-center gap-1.5" data-testid="help-escalated"><CheckCircle2 size={13} className="text-[#4ADE80]" /> Sent to our team. Their reply will appear below.</span>
            ) : escalating ? (
              <div className="flex flex-col sm:flex-row gap-2">
                <input data-testid="help-escalate-note" value={note} onChange={(e) => setNote(e.target.value)} maxLength={1000}
                  placeholder="Anything else our team should know? (optional)" className="pe-input flex-1 rounded-full px-4 py-2 text-sm" />
                <button data-testid="help-escalate-send" onClick={escalate} className="btn-gold px-4 py-2 rounded-full text-sm">Send</button>
              </div>
            ) : (
              <button data-testid="help-escalate-btn" onClick={() => setEscalating(true)} className="text-[#D4AF37] hover:underline flex items-center gap-1.5">
                <UserRound size={13} /> Didn't solve it? Send to our team
              </button>
            )}
          </div>
        )}

        <form onSubmit={(e) => { e.preventDefault(); ask(); }} className="flex items-center gap-3 p-4 border-t border-[#2A2A2A]">
          <input data-testid="help-input" value={text} onChange={(e) => setText(e.target.value)} maxLength={1000}
            placeholder="Ask anything about the app…" className="pe-input flex-1 rounded-full px-5 py-3" />
          <button data-testid="help-send" type="submit" disabled={busy || !text.trim()} className="w-12 h-12 flex-shrink-0 rounded-full gold-gradient flex items-center justify-center text-black disabled:opacity-50">
            {busy ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
          </button>
        </form>
      </div>

      <Tickets tickets={tickets} />
    </div>
  );
}
